import type { CdaStructuredTextRecord } from 'react-datocms/structured-text';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Button from '@carbonify/ui/components/Button/Button';
import Input from '@carbonify/ui/components/Input/Input';
import { createResolver, EmailField, type SchemaFields } from 'constants/validation';

import styles from './SubscriptionBlock.module.css';

interface SubscriptionBlockRecord extends CdaStructuredTextRecord {
  ctaTitle: string;
  webhookUrl: string;
  successLabel: string;
  inputLabel: string;
  errorLabel: string;
  invalidEmailLabel: string;
}

const emailSchema = { ...EmailField };

export default function SubscriptionBlock({
  ctaTitle,
  webhookUrl,
  inputLabel,
  errorLabel,
  successLabel,
  invalidEmailLabel,
}: SubscriptionBlockRecord) {
  const methods = useForm<SchemaFields<typeof emailSchema>>({
    resolver: createResolver(emailSchema),
  });

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
  } = methods;

  const onSubmit: SubmitHandler<SchemaFields<typeof emailSchema>> = async ({ email }) => {
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Subscription webhook failed');
      }

      reset();
      toast.success(successLabel);
    } catch (error) {
      console.error(error);
      toast.error(errorLabel);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        className={styles.form}
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Input
          name="email"
          type="email"
          placeholder={inputLabel}
          error={errors.email?.message && invalidEmailLabel}
          className={styles.input}
          disabled={isSubmitting}
          required
        />
        <Button
          type="submit"
          variant="ghost"
          highlight
          borderRadius="2xl"
          disabled={isSubmitting}
        >
          {ctaTitle}
        </Button>
      </form>
    </FormProvider>
  );
}
